import React, { useRef } from 'react';
import { getApiUrl } from '../../config/apiConfig';

interface UpskillRecommenderCardProps {
  upskillFile: File | null;
  setUpskillFile: React.Dispatch<React.SetStateAction<File | null>>;
  upskillData: any;
  setUpskillData: React.Dispatch<React.SetStateAction<any>>;
  isUploading: boolean;
  setIsUploading: React.Dispatch<React.SetStateAction<boolean>>;
  uploadError: string | null;
  setUploadError: React.Dispatch<React.SetStateAction<string | null>>;
  fileError: string;
  setFileError: React.Dispatch<React.SetStateAction<string>>;
  copySuccess: {[key: string]: string};
  setCopySuccess: React.Dispatch<React.SetStateAction<{[key: string]: string}>>;
  processingDone: {[key: string]: boolean};
  setProcessingDone: React.Dispatch<React.SetStateAction<{[key: string]: boolean}>>;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>, setFile: React.Dispatch<React.SetStateAction<File | null>>) => void;
  copyToClipboard: (text: string, type: string) => void;
  clearForm: (fileType: string) => void;
}

const UpskillRecommenderCard: React.FC<UpskillRecommenderCardProps> = ({
  upskillFile,
  setUpskillFile,
  upskillData,
  setUpskillData,
  isUploading,
  setIsUploading,
  uploadError,
  setUploadError,
  fileError,
  copySuccess,
  processingDone,
  handleFileChange,
  copyToClipboard,
  clearForm
}) => {
  const upskillFileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async () => {
    if (!upskillFile) return;
    
    setIsUploading(true);
    setUploadError(null);
    
    try {
      // Use the API config to get the correct endpoint
      // For upskill, we use the same endpoint as job matcher
      const apiUrl = getApiUrl('JOB_MATCHER');
      
      const formData = new FormData();
      formData.append('file', upskillFile);
      
      // For upskill, we pass an empty string for job description
      formData.append('jobDescription', '');
      
      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          body: formData,
        });
        
        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        setUpskillData(data);
      } catch (fetchError) {
        // Check if it's a network error (connection refused)
        if (fetchError instanceof TypeError && fetchError.message.includes('Failed to fetch')) {
          throw new Error('Backend server is not running. Please start the backend server on port 5275.');
        } else {
          throw fetchError;
        }
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <h2 className="text-xl font-bold mb-4 dark:text-white">Upskill Recommender</h2>
      <p className="text-gray-600 dark:text-gray-300 mb-4">
        Analyzes a resume to find skill gaps and suggest a study guide.
      </p>
      
      <div className="mb-6 border p-4 rounded-md bg-white dark:bg-gray-800 dark:border-gray-700">
        <h3 className="font-medium mb-2 dark:text-white">Upload Resume for Upskill Recommendations</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Upload your resume to get personalized upskill recommendations</p>
        <div className="flex flex-col space-y-4">
          <div className="flex items-center">
            <label htmlFor="upskill-file" className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 py-2 px-4 rounded cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-800/40 transition-colors">
              Choose File
            </label>
            <span className="ml-3 text-gray-500 dark:text-gray-400 text-sm">{upskillFile ? upskillFile.name : 'No file chosen'}</span>
            <input id="upskill-file" type="file" ref={upskillFileInputRef} onChange={(e) => handleFileChange(e, setUpskillFile)} accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden" />
          </div>
          <div className="flex items-center space-x-2">
            <button onClick={handleFileUpload} disabled={!upskillFile || isUploading} className="bg-blue-600 dark:bg-blue-700 text-white py-2 px-4 rounded hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:bg-blue-300 dark:disabled:bg-blue-800 disabled:cursor-not-allowed flex items-center">
              {isUploading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                'Process Resume'
              )}
            </button>
            <button onClick={() => clearForm('upskill')} disabled={!upskillFile && !upskillData} className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-2 px-4 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-600 disabled:cursor-not-allowed">
              Clear
            </button>
          </div>
        </div>
        {fileError && <p className="text-red-500 text-sm mt-2">{fileError}</p>}
        {uploadError && <p className="text-red-500 text-sm mt-2">{uploadError}</p>}
      </div>
      
      {/* Upskill Results */}
      {upskillData && (
        <div className="mb-6 border dark:border-gray-700 p-4 rounded-md bg-white dark:bg-gray-800">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-medium mb-2 dark:text-white">Upskill Recommendations</h3>
            <button 
              onClick={() => {
                // Format the data in AsciiDoc format
                // Ensure all data is in array format
                const generalFeedback = Array.isArray(upskillData.generalFeedback) 
                  ? upskillData.generalFeedback 
                  : typeof upskillData.generalFeedback === 'string' 
                    ? [upskillData.generalFeedback] 
                    : [];
                    
                const possibleUpskills = Array.isArray(upskillData.possibleUpskills) 
                  ? upskillData.possibleUpskills 
                  : typeof upskillData.possibleUpskills === 'string' 
                    ? [upskillData.possibleUpskills] 
                    : [];
                
                const asciiDocContent = `= Upskill Recommendations\n\n== Resume Restructuring Tips\n${generalFeedback.map((item: string) => `* ${item}`).join('\n')}\n\n== Recommended Skills to Develop\n${possibleUpskills.map((item: string) => `* ${item}`).join('\n')}`;
                
                copyToClipboard(asciiDocContent, 'upskill');
              }}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              {copySuccess.upskill || 'Copy to Clipboard'}
            </button>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded overflow-auto">
            {/* Resume Restructuring Tips */}
            <div className="mb-4">
              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Resume Restructuring Tips</h4>
              {(() => {
                // Use generalFeedback property
                const generalFeedback = Array.isArray(upskillData.generalFeedback) 
                  ? upskillData.generalFeedback 
                  : typeof upskillData.generalFeedback === 'string' 
                    ? [upskillData.generalFeedback] 
                    : [];
                    
                return generalFeedback.length > 0 ? (
                  <ul className="list-disc pl-5 space-y-1">
                    {generalFeedback.map((item: string, index: number) => (
                      <li key={index} className="text-blue-700 dark:text-blue-400">{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 italic">No restructuring tips available</p>
                );
              })()}
            </div>
            
            {/* Possible Upskill */}
            <div>
              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Recommended Skills to Develop</h4>
              {(() => {
                // Ensure possibleUpskills is an array
                const possibleUpskills = Array.isArray(upskillData.possibleUpskills) 
                  ? upskillData.possibleUpskills 
                  : typeof upskillData.possibleUpskills === 'string' 
                    ? [upskillData.possibleUpskills] 
                    : [];
                    
                return possibleUpskills.length > 0 ? (
                  <ul className="list-disc pl-5 space-y-1">
                    {possibleUpskills.map((skill: string, index: number) => (
                      <li key={index} className="text-purple-700 dark:text-purple-400">{skill}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 italic">No skill recommendations available</p>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpskillRecommenderCard;
